import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Checkbox,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  ImportExport,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./Allocation.css";

/* ===== EMPTY FORM (IMAGE BASED FIELDS) ===== */
const emptyForm = {
  location: "",
  assetsType: "",
  description: "",
  quantity: "",
  employeeId: "",
  employeeName: "",
  issuedDate: "",
  status: "",
  updatedBy: "",
  updatedDate: "",
};

/* ===== SAMPLE DATA (IMAGE SAME) ===== */
const initialData = [
  {
    location: "Hyderabad",
    assetsType: "Hardware",
    description: "Dell Latitude Laptop",
    quantity: "1",
    employeeId: "EMP001",
    employeeName: "Rahul Sharma",
    issuedDate: "2025-01-10",
    status: "Assigned",
    updatedBy: "Admin",
    updatedDate: "2025-01-10",
  },
  {
    location: "Pune",
    assetsType: "Software",
    description: "Windows 11 Pro License",
    quantity: "1",
    employeeId: "EMP002",
    employeeName: "Anita Verma",
    issuedDate: "2025-01-15",
    status: "Aborted",
    updatedBy: "HR Manager",
    updatedDate: "2025-01-16",
  },
  {
    location: "Chennai",
    assetsType: "Stationary",
    description: "Notebook, Pen, ID Card",
    quantity: "5",
    employeeId: "EMP003",
    employeeName: "Suresh Kumar",
    issuedDate: "2025-01-18",
    status: "Assigned",
    updatedBy: "Store Admin",
    updatedDate: "2025-01-18",
  },
  {
    location: "Bangalore",
    assetsType: "MSC",
    description: "Microsoft Office 365 Subscription",
    quantity: "1",
    employeeId: "EMP004",
    employeeName: "Pooja Singh",
    issuedDate: "2025-01-20",
    status: "Assigned",
    updatedBy: "IT Support",
    updatedDate: "2025-01-20",
  },
  {
    location: "IIA",
    assetsType: "Hardware",
    description: "HP LaserJet Printer",
    quantity: "1",
    employeeId: "EMP005",
    employeeName: "Amit Patel",
    issuedDate: "2025-01-22",
    status: "In Stock",
    updatedBy: "Warehouse",
    updatedDate: "2025-01-22",
  },
];


export default function AssetsAllocation() {
  const [data, setData] = useState(initialData);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [editIndex, setEditIndex] = useState(null);
  const fileRef = useRef(null);

  const openModal = (type, row = null, index = null) => {
    setMode(type);
    setForm(row || emptyForm);
    setEditIndex(index);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setForm(emptyForm);
    setEditIndex(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (mode === "add") {
      setData([...data, form]);
    } else {
      const updated = [...data];
      updated[editIndex] = form;
      setData(updated);
    }
    closeModal();
  };

  const handleDelete = (index) => {
    if (window.confirm("Delete this record?")) {
      setData(data.filter((_, i) => i !== index));
    }
  };

  /* ===== EXPORT ===== */
  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Allocation");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "asset-allocation.xlsx");
  };

  /* ===== IMPORT ===== */
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      setData(XLSX.utils.sheet_to_json(sheet));
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="alx-wrapper">
      <input type="file" hidden ref={fileRef} onChange={handleImport} />

      {/* HEADER */}
      <div className="alx-header">
        <h2>Assets Allocation</h2>
        <div className="alx-actions">
          <Button onClick={() => fileRef.current.click()} startIcon={<ImportExport />}>
            Import
          </Button>
          <Button onClick={handleExport} startIcon={<ImportExport />}>
            Export
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => openModal("add")}>
            Add
          </Button>
        </div>
      </div>

      {/* TABLE */}
      <TableContainer component={Paper} className="alx-table-wrap">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell><Checkbox /></TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Assets Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Employee Name</TableCell>
              <TableCell>Issued Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell>Updated Date</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                <TableCell><Checkbox /></TableCell>
                {Object.keys(emptyForm).map((key) => (
                  <TableCell key={key}>{row[key]}</TableCell>
                ))}
                <TableCell align="center">
                  <IconButton onClick={() => openModal("view", row)}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => openModal("edit", row, index)} color="warning">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(index)} color="error">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODAL */}
      <Modal open={open} onClose={closeModal}>
        <Box className="alx-modal">
          <div className="alx-modal-header">
            <h3>{mode.toUpperCase()} ALLOCATION</h3>
            <IconButton onClick={closeModal}><Close /></IconButton>
          </div>

          <div className="alx-modal-grid">
            {Object.keys(emptyForm).map((key) => (
              <TextField
                key={key}
                name={key}
                label={key.replace(/([A-Z])/g, " $1")}
                value={form[key]}
                onChange={handleChange}
                disabled={mode === "view"}
                size="small"
              />
            ))}
          </div>

          {mode !== "view" && (
            <div className="alx-modal-footer">
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
}
